import type React from "react";
import { useEffect } from "react";
import { shallowEqual } from "react-redux";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import Chatbot from "src/features/chatbot/components/Chatbot";
import GptConversationLoader, {
  GPT_CONVERSATION_LOADER_COMPONENT_ID,
} from "src/features/chatbot/components/GptConversationLoader";
import RagAgentMultiSelect from "src/features/rag/components/RagAgentMultiSelect";
import {
  fetchAgentsThunk,
  getRagAgents,
  getRagAgentsStatus,
} from "src/features/rag/state/ragSlice";
import {
  setActiveGptConversation,
  getGptConversationsSortedByRecency,
  getActiveGptConversation,
  fetchGptConversations,
  resetChat,
  getScenarioHelperRagAgentIds,
  setScenarioHelperRagAgentIds,
} from "src/features/chatbot/state/chatbotSlice";

interface ScenarioHelperWorkbenchProps {
  lockedDestinationProjectGroupId?: number;
  disableAutoResume?: boolean;
}

const ScenarioHelperWorkbench: React.FC<ScenarioHelperWorkbenchProps> = ({
  lockedDestinationProjectGroupId,
  disableAutoResume,
}) => {
  const dispatch = useAppDispatch();
  const userPreviousGptConversations = useAppSelector(getGptConversationsSortedByRecency);
  const userActiveGptConversation = useAppSelector(getActiveGptConversation);
  const ragAgentIds = useAppSelector(getScenarioHelperRagAgentIds, shallowEqual);
  const agents = useAppSelector(getRagAgents);
  const agentsStatus = useAppSelector(getRagAgentsStatus);

  const handleConversationSelect = (conversationId: number) => {
    const selectedConversation = userPreviousGptConversations.find(
      (conv) => conv.id === conversationId,
    );
    if (selectedConversation) {
      dispatch(setActiveGptConversation(selectedConversation));
    }
  };

  const preSelectSophieIfAvailable = () => {
    const sophie = agents.find((a) => a.is_shared && a.name.toLowerCase() === "sophie");
    if (sophie) {
      dispatch(setScenarioHelperRagAgentIds([sophie.id]));
    }
  };

  const handleResetChat = () => {
    void dispatch(resetChat({ cb: resetChatLocally })).then(() => {
      void dispatch(
        fetchGptConversations({ componentId: GPT_CONVERSATION_LOADER_COMPONENT_ID, start: 0 }),
      );
      preSelectSophieIfAvailable();
    });
  };

  const resetChatLocally = () => {
    setActiveGptConversation(null);
  };

  useEffect(() => {
    void dispatch(
      fetchGptConversations({ componentId: GPT_CONVERSATION_LOADER_COMPONENT_ID, start: 0 }),
    );
  }, [dispatch]);

  useEffect(() => {
    void dispatch(fetchAgentsThunk());
  }, [dispatch]);

  useEffect(() => {
    if (userActiveGptConversation) return;
    if (agentsStatus !== "loaded") return;
    preSelectSophieIfAvailable();
  }, [userActiveGptConversation, agentsStatus, agents]);

  return (
    <div className="flex h-full w-full flex-col overflow-hidden">
      <div
        className="grid min-h-0 w-full flex-1 overflow-hidden"
        style={{ gridTemplateColumns: "240px 1fr 280px" }}
      >
        <div className="bg-gray-100 px-3 pt-1 border-r border-gray-200 overflow-y-auto overflow-x-hidden">
          <GptConversationLoader
            onConversationSelect={handleConversationSelect}
            onResetChat={handleResetChat}
          />
        </div>

        <div className="min-w-0 overflow-hidden">
          <Chatbot
            mode="scenarioHelper"
            activeConversation={userActiveGptConversation}
            lockedDestinationProjectGroupId={lockedDestinationProjectGroupId}
            disableAutoResume={disableAutoResume}
          />
        </div>

        <div className="bg-gray-50 border-l border-gray-200 p-4 pb-32 flex flex-col min-h-0 overflow-x-hidden overflow-y-hidden">
          <RagAgentMultiSelect
            fillHeight
            value={ragAgentIds}
            onChange={(ids) => dispatch(setScenarioHelperRagAgentIds(ids))}
          />
        </div>
      </div>
    </div>
  );
};

export default ScenarioHelperWorkbench;
